import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Users, Flame, AlertTriangle, Trophy, Target, ChevronRight, Loader2, X, Megaphone, Zap, Award, RefreshCw } from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub, color, delay = 0, onClick }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      onClick={onClick}
      className="bg-white border border-[#E2E8F0] rounded-xl p-4 text-left hover:border-[#2563EB] hover:shadow-md transition-all group"
      data-testid={`statcard-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
            <Icon size={16} strokeWidth={2.5} />
          </div>
          <span className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold">{label}</span>
        </div>
        <ChevronRight size={12} className="text-[#CBD5E1] group-hover:text-[#2563EB] transition-colors" />
      </div>
      <p className="font-['Barlow_Condensed'] text-2xl font-black text-[#0A2242]" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
        {value}
      </p>
      {sub && <p className="text-[10px] text-[#64748B] mt-0.5">{sub}</p>}
    </motion.button>
  );
}

function StreakBar({ label, count, total, color, onClick, testid }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <button
      onClick={onClick}
      disabled={count === 0}
      className="w-full flex items-center gap-3 group disabled:cursor-default"
      data-testid={testid}
    >
      <span className="text-[10px] text-[#64748B] font-bold w-14 text-right">{label}</span>
      <div className="flex-1 bg-[#F1F5F9] rounded-full h-3 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${color} ${count > 0 ? 'group-hover:opacity-80' : ''}`}
          style={{ width: `${Math.max(pct, pct > 0 ? 6 : 0)}%` }}
        />
      </div>
      <span className={`text-xs font-bold w-6 text-right ${count > 0 ? 'text-[#0A2242] group-hover:text-[#2563EB]' : 'text-[#94A3B8]'}`}>{count}</span>
    </button>
  );
}

function PlayerListModal({ open, title, subtitle, players, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="analytics-modal-backdrop"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col shadow-xl"
        >
          <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-[#E2E8F0]">
            <div>
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-[#0A2242]" data-testid="analytics-modal-title">{title}</h3>
              {subtitle && <p className="text-[11px] text-[#64748B] mt-0.5">{subtitle}</p>}
            </div>
            <button onClick={onClose} className="text-[#64748B] hover:text-[#0A2242]" data-testid="analytics-modal-close">
              <X size={20} />
            </button>
          </div>
          <div className="overflow-y-auto px-4 py-3 space-y-1.5">
            {players.length === 0 ? (
              <p className="text-center py-10 text-[#94A3B8] text-sm">No players in this bucket.</p>
            ) : (
              players.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { onClose(); navigate(`/coach/player/${p.id}`); }}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-[#F8FAFC] hover:bg-[#EFF6FF] transition-colors text-left"
                  data-testid={`modal-player-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#0A2242] truncate">{p.name}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {p.position && <span className="text-[9px] text-[#2563EB] font-bold uppercase">{p.position}</span>}
                      {p.birth_year && <span className="text-[9px] text-[#64748B]">'{String(p.birth_year).slice(-2)}</span>}
                      <span className="text-[9px] text-[#64748B]">{p.drills_completed} drills this wk</span>
                      {p.days_inactive > 0 && p.days_inactive < 900 && (
                        <span className="text-[9px] text-[#EF4444] font-bold">{p.days_inactive}d inactive</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {p.avg_score > 0 && <p className="text-xs font-bold text-[#10B981]">{p.avg_score}/30</p>}
                    <div className="flex items-center gap-0.5 justify-end">
                      <Flame size={9} className="text-[#FF9100]" />
                      <span className="text-[9px] text-[#FF9100] font-bold">{p.current_streak}</span>
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-[#CBD5E1]" />
                </button>
              ))
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function RealtimeStatsCard({ stats, loading, onRefresh }) {
  const fmt = (n) => (n ?? 0).toLocaleString();
  const asOf = stats?.as_of ? new Date(stats.as_of) : null;

  // Sparkline data: last 30 days of signups
  const days = stats?.daily_signups_30d || [];
  const maxDaily = days.reduce((m, d) => Math.max(m, d.count || 0), 0) || 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-[#0A2242] to-[#0F2D5C] rounded-2xl p-5 text-white shadow-lg"
      data-testid="realtime-stats-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Zap size={14} className="text-[#10B981]" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Live from your DB</p>
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold tracking-tight">REAL-TIME STATS</h3>
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-50"
          aria-label="Refresh stats"
          data-testid="refresh-stats-btn"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {!stats && loading && (
        <div className="flex justify-center py-6">
          <Loader2 size={20} className="animate-spin text-white/60" />
        </div>
      )}

      {stats && (
        <>
          {/* Top row: Totals (3 columns) */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-white/5 rounded-lg px-2 py-2.5 text-center" data-testid="stat-total-players">
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8] mb-0.5">Players</div>
              <div className="font-['Barlow_Condensed'] text-2xl font-black leading-none">{fmt(stats.totals.total_players)}</div>
            </div>
            <div className="bg-white/5 rounded-lg px-2 py-2.5 text-center" data-testid="stat-active-subs">
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#10B981] mb-0.5">Paying</div>
              <div className="font-['Barlow_Condensed'] text-2xl font-black leading-none text-[#10B981]">{fmt(stats.totals.active_subscribers)}</div>
            </div>
            <div className="bg-white/5 rounded-lg px-2 py-2.5 text-center" data-testid="stat-conversion">
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8] mb-0.5">Conv.</div>
              <div className="font-['Barlow_Condensed'] text-2xl font-black leading-none">{stats.conversion_rate}%</div>
            </div>
          </div>

          {/* Middle row: Founders + Coaches */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-white/5 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="stat-founders">
              <Award size={14} className="text-[#FF9100] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">Founders</div>
                <div className="text-sm font-bold">
                  <span className="text-[#FF9100]">{fmt(stats.totals.founders_awarded)}</span>
                  <span className="text-[#64748B]"> / 1,000</span>
                </div>
              </div>
            </div>
            <div className="bg-white/5 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="stat-coaches">
              <Users size={14} className="text-[#00E5FF] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">Coaches</div>
                <div className="text-sm font-bold">{fmt(stats.totals.total_coaches)}</div>
              </div>
            </div>
          </div>

          {/* Signup window */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="text-center" data-testid="signups-today">
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">Today</div>
              <div className="font-['Barlow_Condensed'] text-lg font-bold">+{fmt(stats.signups.today)}</div>
            </div>
            <div className="text-center" data-testid="signups-7d">
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">7d</div>
              <div className="font-['Barlow_Condensed'] text-lg font-bold">+{fmt(stats.signups.last_7d)}</div>
            </div>
            <div className="text-center" data-testid="signups-30d">
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">30d</div>
              <div className="font-['Barlow_Condensed'] text-lg font-bold">+{fmt(stats.signups.last_30d)}</div>
            </div>
          </div>

          {/* 30-day sparkline */}
          {days.length > 0 && (
            <div data-testid="signups-sparkline">
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">Daily signups (last 30 days)</span>
                <span className="text-[9px] text-[#64748B]">peak: {maxDaily}</span>
              </div>
              <div className="flex items-end gap-[2px] h-12">
                {days.map((d) => {
                  const h = Math.max(2, Math.round((d.count / maxDaily) * 100));
                  return (
                    <div
                      key={d.date}
                      className="flex-1 bg-gradient-to-t from-[#2563EB] to-[#00E5FF] rounded-sm hover:opacity-80 transition-opacity"
                      style={{ height: `${h}%`, opacity: d.count === 0 ? 0.15 : 1 }}
                      title={`${d.date}: ${d.count} signup${d.count === 1 ? '' : 's'}`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between mt-1 text-[9px] text-[#64748B] tabular-nums">
                <span>{days[0]?.date.slice(5)}</span>
                <span>{days[days.length - 1]?.date.slice(5)}</span>
              </div>
            </div>
          )}

          {asOf && (
            <p className="text-[9px] text-[#64748B] mt-3 text-center">
              As of {asOf.toLocaleString()}
            </p>
          )}
        </>
      )}
    </motion.div>
  );
}

export default function CoachAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { title, subtitle, players }
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await adminAPI.realtimeStats();
      setStats(res.data);
    } catch { /* ignore */ }
    setStatsLoading(false);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await adminAPI.analytics();
        setData(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
    loadStats();
  }, []);

  if (loading) {
    return (
      <Layout title="ANALYTICS" showBack>
        <div className="flex justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[#2563EB]" />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout title="ANALYTICS" showBack>
        <div className="px-4 py-12 text-center">
          <p className="text-[#64748B] text-sm">Failed to load analytics.</p>
        </div>
      </Layout>
    );
  }

  const { streak_distribution: sd, player_breakdown: all = [] } = data;
  const openModal = (title, subtitle, players) => setModal({ title, subtitle, players });

  return (
    <Layout title="ANALYTICS" showBack>
      <div className="px-4 py-6 space-y-5">
        {/* Real-time Stats Card */}
        <RealtimeStatsCard stats={stats} loading={statsLoading} onRefresh={loadStats} />

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-[#64748B] text-xs font-bold uppercase tracking-widest">Week Overview</p>
          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold tracking-tight mt-1 text-[#0A2242]" data-testid="analytics-title">
            TEAM ANALYTICS
          </h2>
          <p className="text-[10px] text-[#94A3B8] mt-1">Tap any stat to see which players are in that group.</p>
        </motion.div>

        {/* Key Metrics — every card clickable */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            icon={Target}
            label="Completion"
            value={`${data.completion_rate}%`}
            sub={`${data.weekly_drill_stats.completed} of ${data.weekly_drill_stats.total} drills`}
            color="bg-[#2563EB]/10 text-[#2563EB]"
            delay={0}
            onClick={() => openModal(
              'Completion by Player',
              `This week's drill completions per player (${data.weekly_drill_stats.total} total expected)`,
              all
            )}
          />
          <StatCard
            icon={Users}
            label="Active Players"
            value={data.total_players}
            sub={`${data.total_players - data.needs_attention.length} on track`}
            color="bg-[#10B981]/10 text-[#10B981]"
            delay={0.05}
            onClick={() => openModal(
              'Active Players',
              'All paid players on track this week',
              all.filter(p => !data.needs_attention.find(n => n.id === p.id))
            )}
          />
          <StatCard
            icon={Flame}
            label="Avg Streak"
            value={`${data.avg_streak} days`}
            sub={`${sd.medium + sd.long} players 4+ days`}
            color="bg-[#FF9100]/10 text-[#FF9100]"
            delay={0.1}
            onClick={() => openModal(
              'Streaks (highest first)',
              `Avg ${data.avg_streak} days across ${data.total_players} players`,
              [...all].sort((a, b) => b.current_streak - a.current_streak)
            )}
          />
          <StatCard
            icon={AlertTriangle}
            label="Need Attention"
            value={data.needs_attention.length}
            sub="Inactive or struggling"
            color="bg-[#EF4444]/10 text-[#EF4444]"
            delay={0.15}
            onClick={() => openModal(
              'Needs Attention',
              'Inactive 3+ days, zero activity this week, or low avg score',
              data.needs_attention
            )}
          />
        </div>

        {/* Streak Distribution — each bar clickable */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4"
        >
          <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B] mb-3" data-testid="streak-dist-title">
            Streak Distribution <span className="text-[#94A3B8] font-normal normal-case tracking-normal">(tap to see players)</span>
          </h3>
          <div className="space-y-2.5">
            <StreakBar
              label="0 days" count={sd.zero} total={data.total_players} color="bg-[#EF4444]"
              testid="streak-bar-zero"
              onClick={() => openModal('Streak: 0 days', `${sd.zero} player${sd.zero === 1 ? '' : 's'} with no current streak`, all.filter(p => p.current_streak === 0))}
            />
            <StreakBar
              label="1-3" count={sd.short} total={data.total_players} color="bg-[#FF9100]"
              testid="streak-bar-short"
              onClick={() => openModal('Streak: 1-3 days', `${sd.short} player${sd.short === 1 ? '' : 's'}`, all.filter(p => p.current_streak >= 1 && p.current_streak <= 3))}
            />
            <StreakBar
              label="4-7" count={sd.medium} total={data.total_players} color="bg-[#2563EB]"
              testid="streak-bar-medium"
              onClick={() => openModal('Streak: 4-7 days', `${sd.medium} player${sd.medium === 1 ? '' : 's'}`, all.filter(p => p.current_streak >= 4 && p.current_streak <= 7))}
            />
            <StreakBar
              label="8+" count={sd.long} total={data.total_players} color="bg-[#10B981]"
              testid="streak-bar-long"
              onClick={() => openModal('Streak: 8+ days', `${sd.long} player${sd.long === 1 ? '' : 's'} on a long streak 🔥`, all.filter(p => p.current_streak >= 8))}
            />
          </div>
        </motion.div>

        {/* Acquisition — How did players find us? */}
        {data.acquisition && data.acquisition.sources.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            className="bg-white border border-[#E2E8F0] rounded-xl p-4"
          >
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Megaphone size={14} className="text-[#00E5FF]" />
                <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B]" data-testid="acquisition-title">
                  Acquisition Mix
                </h3>
              </div>
              <span className="text-[10px] text-[#94A3B8]">
                {data.acquisition.total_with_data} of {data.acquisition.total_players} reported
              </span>
            </div>
            <div className="space-y-2">
              {data.acquisition.sources.map((s, i) => {
                const pct = data.acquisition.total_players > 0
                  ? (s.count / data.acquisition.total_players) * 100
                  : 0;
                const isUnknown = s.source === 'Unknown / Pre-launch';
                const palette = [
                  'bg-[#00E5FF]', 'bg-[#FF9100]', 'bg-[#10B981]', 'bg-[#A78BFA]',
                  'bg-[#2563EB]', 'bg-[#F472B6]', 'bg-[#EAB308]', 'bg-[#EF4444]',
                ];
                const color = isUnknown ? 'bg-[#CBD5E1]' : palette[i % palette.length];
                return (
                  <button
                    key={s.source}
                    onClick={() => openModal(
                      `${s.source} (${s.count})`,
                      `Players who found Elev802 Connected via ${s.source}`,
                      // Map acquisition entries to the modal's expected shape
                      s.players.map(p => ({
                        ...p,
                        drills_completed: 0, // unknown at this layer, modal still shows core info
                        current_streak: 0,
                        avg_score: 0,
                        days_inactive: 999,
                      }))
                    )}
                    className="w-full flex items-center gap-3 group"
                    data-testid={`acquisition-bar-${i}`}
                  >
                    <span className="text-[10px] text-[#0A2242] font-bold w-32 text-left truncate" title={s.source}>
                      {s.source}
                    </span>
                    <div className="flex-1 bg-[#F1F5F9] rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${color} group-hover:opacity-80`}
                        style={{ width: `${Math.max(pct, pct > 0 ? 6 : 0)}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-[#0A2242] w-10 text-right group-hover:text-[#2563EB]">
                      {s.count}
                    </span>
                    <span className="text-[10px] text-[#94A3B8] w-10 text-right">
                      {pct.toFixed(0)}%
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Top Performers */}
        {data.top_performers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white border border-[#E2E8F0] rounded-xl p-4"
          >
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Trophy size={14} className="text-[#FF9100]" />
                <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B]" data-testid="top-performers-title">
                  Top Performers
                </h3>
              </div>
              <button
                onClick={() => openModal(
                  'All Players by Performance',
                  'Every player with activity this week, sorted by avg score + streak',
                  [...all].filter(p => p.drills_completed > 0).sort((a, b) => b.avg_score - a.avg_score || b.current_streak - a.current_streak)
                )}
                className="text-[10px] font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8]"
                data-testid="see-all-performers"
              >
                See all
              </button>
            </div>
            <div className="space-y-2">
              {data.top_performers.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/coach/player/${p.id}`)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors text-left"
                  data-testid={`top-performer-${i}`}
                >
                  <span className={`text-xs font-black w-5 text-center ${
                    i === 0 ? 'text-yellow-500' : i === 1 ? 'text-gray-400' : i === 2 ? 'text-amber-600' : 'text-[#64748B]'
                  }`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#0A2242] truncate">{p.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {p.position && <span className="text-[9px] text-[#2563EB] font-bold uppercase">{p.position}</span>}
                      <span className="text-[9px] text-[#64748B]">{p.drills_completed} drills</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-black text-[#10B981]">{p.avg_score}/30</p>
                    <div className="flex items-center gap-0.5 justify-end">
                      <Flame size={9} className="text-[#FF9100]" />
                      <span className="text-[9px] text-[#FF9100] font-bold">{p.current_streak}</span>
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-[#CBD5E1] flex-shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Needs Attention */}
        {data.needs_attention.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-[#EF4444]/20 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={14} className="text-[#EF4444]" />
              <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#EF4444]" data-testid="needs-attention-title">
                Needs Attention
              </h3>
            </div>
            <div className="space-y-2">
              {data.needs_attention.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/coach/player/${p.id}`)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-[#FEF2F2] hover:bg-[#FEE2E2] transition-colors text-left"
                  data-testid={`needs-attention-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#0A2242] truncate">{p.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {p.reasons.map((r, ri) => (
                        <span key={ri} className="text-[9px] bg-[#EF4444]/10 text-[#EF4444] px-1.5 py-0.5 rounded font-bold">
                          {r}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-[#CBD5E1] flex-shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Full roster shortcut */}
        <button
          onClick={() => openModal(
            `All ${data.total_players} Players`,
            'Full roster for this week — tap any to open profile',
            all
          )}
          className="w-full flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8] bg-white border border-[#E2E8F0] rounded-xl py-3 transition-colors"
          data-testid="view-full-roster-btn"
        >
          <TrendingUp size={12} /> View full roster breakdown
        </button>
      </div>

      <PlayerListModal
        open={!!modal}
        title={modal?.title || ''}
        subtitle={modal?.subtitle}
        players={modal?.players || []}
        onClose={() => setModal(null)}
      />
    </Layout>
  );
}
